import { useCallback, useEffect, useState } from 'react'
import { fetchSessions, type SessionSummary } from './api'

const PAGE = 50

function fmtTime(ts: number): string {
  return new Date(ts).toLocaleString()
}

function preview(s: string | null, n = 90): string {
  if (!s) return '—'
  const trimmed = s.replace(/\s+/g, ' ').trim()
  return trimmed.length > n ? trimmed.slice(0, n) + '…' : trimmed
}

// Display labels for the server-derived acquisition channel. 'other' falls
// back to the raw utm_source / referrer host at render time so an unmapped
// channel is still identifiable instead of a dead-end "Other".
const SOURCE_LABEL: Record<SessionSummary['source_channel'], string> = {
  google_ads: 'G Ads',
  google_organic: 'Google',
  reddit: 'Reddit',
  openai: 'GPT',
  x: 'X',
  other: 'Other',
  direct: '—',
}

function referrerHost(referrer: string | null): string | null {
  if (!referrer) return null
  try {
    return new URL(referrer).hostname.replace(/^www\./, '')
  } catch {
    return referrer
  }
}

function sourceLabel(s: SessionSummary): string {
  if (s.source_channel === 'other') {
    return s.utm_source ?? referrerHost(s.referrer) ?? 'Other'
  }
  return SOURCE_LABEL[s.source_channel] ?? s.source_channel
}

// Hover detail: the raw first-touch attribution behind the bucket.
function sourceTitle(s: SessionSummary): string {
  const parts: string[] = []
  if (s.utm_source) parts.push(`utm_source=${s.utm_source}`)
  if (s.utm_campaign) parts.push(`campaign=${s.utm_campaign}`)
  if (s.utm_term) parts.push(`term=${s.utm_term}`)
  if (s.referrer) parts.push(`referrer=${s.referrer}`)
  return parts.length ? parts.join('\n') : 'no attribution captured'
}

// Which CTA surface(s) the session converted on, most-committal first. A
// session can tap more than one (e.g. WhatsApp then SMS). WhatsApp leads
// because it's the primary US CTA since 2026-07-22; without it a WhatsApp tap
// would read as a bare ✓ with no channel.
function ctaChannels(s: SessionSummary): string[] {
  const out: string[] = []
  if (s.cta_wa) out.push('WA')
  if (s.cta_sms) out.push('SMS')
  if (s.cta_email) out.push('email')
  return out
}

export function SessionList({
  token,
  exclude,
  onOpen,
}: {
  token: string
  exclude: boolean
  onOpen: (id: string) => void
}) {
  const [sessions, setSessions] = useState<SessionSummary[]>([])
  const [total, setTotal] = useState(0)
  const [offset, setOffset] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(
    async (off: number) => {
      setLoading(true)
      setError(null)
      try {
        const data = await fetchSessions(token, { limit: PAGE, offset: off, exclude })
        setSessions(data.sessions)
        setTotal(data.total)
        setOffset(data.offset)
      } catch (e) {
        setError(e instanceof Error ? e.message : 'failed')
      } finally {
        setLoading(false)
      }
    },
    [token, exclude]
  )

  useEffect(() => {
    void load(0)
  }, [load])

  const lastShown = Math.min(offset + sessions.length, total)
  const hasPrev = offset > 0
  const hasNext = offset + PAGE < total

  return (
    <section className="panel">
      <div className="panel-head">
        <h2>Recent sessions</h2>
        <div className="muted small">
          {total > 0
            ? `${offset + 1}–${lastShown} of ${total}`
            : loading
              ? 'Loading…'
              : 'No sessions'}
        </div>
      </div>

      {error && <div className="admin-error">{error}</div>}

      <table className="data sessions">
        <thead>
          <tr>
            <th>Updated</th>
            <th>Country</th>
            <th title="Acquisition channel from first-touch attribution — G Ads = Google Ads click (gclid), Google = organic search referrer, Reddit / GPT (ChatGPT) / X from utm or referrer. Hover a value for the raw utm/referrer.">Source</th>
            <th title="User reported a GLP-1 medication (e.g. Ozempic, Wegovy, Mounjaro) during the calc interview">GLP-1</th>
            <th className="num">Msgs</th>
            <th className="num">Calcs</th>
            <th title="A plan markdown was generated for this session">Plan</th>
            <th title="Which post-plan CTA the user converted on — WA = WhatsApp deep link (primary US CTA since 2026-07-22), SMS = SMS claim link, email = email form (non-US). Blank = no CTA action.">CTA</th>
            <th>First user message</th>
          </tr>
        </thead>
        <tbody>
          {sessions.length === 0 && !loading && (
            <tr>
              <td colSpan={9} className="muted">
                No sessions in range.
              </td>
            </tr>
          )}
          {sessions.map((s) => (
            <tr
              key={s.id}
              className="session-row"
              onClick={() => onOpen(s.id)}
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  onOpen(s.id)
                }
              }}
            >
              <td className="tabular">{fmtTime(s.updated_at)}</td>
              <td>{s.country ?? '—'}</td>
              <td className="source-cell">
                {s.source_channel === 'direct' ? (
                  <span className="muted">—</span>
                ) : (
                  <span
                    className={`src-pill src-${s.source_channel}`}
                    title={sourceTitle(s)}
                  >
                    {sourceLabel(s)}
                  </span>
                )}
              </td>
              <td className="glp1-flag">
                {s.is_glp1 ? <span className="glp1-pill">GLP-1</span> : '—'}
              </td>
              <td className="num tabular">{s.message_count}</td>
              <td className="num tabular">{s.calc_count}</td>
              <td className={`funnel-flag${s.has_plan ? ' on' : ''}`}>
                {s.has_plan ? '✓' : '—'}
              </td>
              <td className={`funnel-flag${s.cta_clicked ? ' on' : ''}`}>
                {s.cta_clicked ? (
                  <span className="cta-channels">
                    {(ctaChannels(s).length ? ctaChannels(s) : ['✓']).map((ch) => (
                      <span key={ch} className={`cta-chan cta-chan-${ch.toLowerCase()}`}>
                        {ch}
                      </span>
                    ))}
                  </span>
                ) : (
                  '—'
                )}
              </td>
              <td className="preview">{preview(s.first_user_msg)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="pager">
        <button
          className="ghost"
          disabled={!hasPrev || loading}
          onClick={() => load(Math.max(0, offset - PAGE))}
        >
          ← Prev
        </button>
        <button
          className="ghost"
          disabled={!hasNext || loading}
          onClick={() => load(offset + PAGE)}
        >
          Next →
        </button>
      </div>
    </section>
  )
}
